// client/src/components/pages/LexiStar/ReviewQueue/BulkTriageDialog.jsx
//
// Bulk triage (LS-09 §5.18 "bulk triage for identical causes"): resolve every
// open item that shares one cause (e.g. every arrival at one leaking geofence),
// or a hand-picked selection, with one outcome and one reason. Two steps so the
// count is confirmed before anything is written:
//   1. outcome (accept / dismiss) + reason
//   2. "Accept 14 items?" — the confirm button carries the count
// POST /review-items:bulk { cause_key | ids, outcome, reason }.

import React, { useEffect, useState } from 'react';
import { Modal, ReasonField, InlineError, buttonClasses } from './assuranceAtoms';

const OUTCOMES = [
  { value: 'accept', label: 'Accept as recorded', verb: 'Accept' },
  { value: 'dismiss', label: 'Dismiss', verb: 'Dismiss' },
];

export default function BulkTriageDialog({ open, target, busy, error, onCancel, onConfirm }) {
  const [outcome, setOutcome] = useState('accept');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(1);
  useEffect(() => {
    if (open) {
      setOutcome('accept');
      setReason('');
      setStep(1);
    }
  }, [open]);
  if (!open || !target) return null;
  const count = target.count;
  const verb = OUTCOMES.find((o) => o.value === outcome).verb;
  const noun = `${count} item${count === 1 ? '' : 's'}`;

  return (
    <Modal
      open={open}
      title={target.cause_key ? `Triage by cause: ${target.label}` : `Triage ${noun}`}
      onClose={onCancel}
      footer={
        step === 1 ? (
          <>
            <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!reason.trim() || target.counting || !count}
              className={buttonClasses.primary}
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setStep(1)} className={buttonClasses.secondary}>
              Back
            </button>
            <button
              type="button"
              onClick={() => onConfirm({ outcome, reason: reason.trim() })}
              disabled={busy}
              className={buttonClasses.primary}
            >
              {busy ? 'Saving…' : `${verb} ${noun}`}
            </button>
          </>
        )
      }
    >
      {step === 1 ? (
        <>
          <p className="text-sm text-slate-700">
            {target.cause_key ? (
              <>
                <span className="font-semibold tabular-nums">
                  {target.counting ? 'Counting…' : noun}
                </span>{' '}
                open in the queue share the cause{' '}
                <span className="font-mono text-xs">{target.cause_key}</span>. One decision resolves
                them all; each keeps its own audit row.
              </>
            ) : (
              <>
                <span className="font-semibold tabular-nums">{noun}</span> selected. One decision
                resolves them all; each keeps its own audit row.
              </>
            )}
          </p>
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 mb-1.5">Outcome</legend>
            <div className="flex flex-wrap gap-2">
              {OUTCOMES.map((o) => (
                <label
                  key={o.value}
                  className={`inline-flex items-center gap-2 px-3 min-h-[44px] rounded-lg border text-sm cursor-pointer ${outcome === o.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-700'}`}
                >
                  <input
                    type="radio"
                    name="bulk-outcome"
                    value={o.value}
                    checked={outcome === o.value}
                    onChange={() => setOutcome(o.value)}
                    className="sr-only"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              Corrections are made one record at a time — open an item to split, merge, void,
              reclassify or reassign it.
            </p>
          </fieldset>
          <ReasonField value={reason} onChange={setReason} autoFocus />
        </>
      ) : (
        <div role="alert" className="space-y-2">
          <p className="text-base font-semibold text-slate-900">
            {verb} <span className="tabular-nums">{noun}</span>?
          </p>
          <p className="text-sm text-slate-600">
            Reason: “{reason.trim()}”. They leave the queue and{' '}
            {outcome === 'accept'
              ? 'start counting in reports and statements as recorded.'
              : 'are left out of reports and statements.'}
            {target.cause_key &&
              ' Items with this cause that open after now are not included — they arrive in the queue as usual.'}
          </p>
        </div>
      )}
      <InlineError message={error} />
    </Modal>
  );
}
